import * as Yup from 'yup';

import { AuthorizationPeriodsBySessionType } from './types';

interface ValidatedTimeSlot {
	days?: Array<number | undefined>;
	endTime: string;
	startTime: string;
}

const hasOverlappingDailyTimes = (slots: ValidatedTimeSlot[] = []) => {
	const byDay = new Map<number, Array<{ start: string; end: string }>>();
	slots.forEach((slot) => slot.days?.forEach((day) => {
		if (day === undefined) return;
		const entries = byDay.get(day) ?? [];
		entries.push({ start: slot.startTime, end: slot.endTime });
		byDay.set(day, entries);
	}));

	return Array.from(byDay.values()).some((entries) => {
		const sorted = entries.filter((entry) => entry.start && entry.end).sort((left, right) => left.start.localeCompare(right.start));
		return sorted.some((entry, index) => index > 0 && sorted[index - 1].end > entry.start);
	});
};

export type AuthBounds = AuthorizationPeriodsBySessionType;

export const buildValidationSchema = (authBounds: AuthBounds) =>
	Yup.object({
		clientId: Yup.string().required('Please select a client'),
		schedules: Yup.array()
			.of(
				Yup.object({
					startDate: Yup.string()
						.required('Start date is required')
						.test('within-auth', 'Start date must fall within an authorization period', function (value) {
							if (!value) return true;

							const periods = authBounds[this.parent.sessionTypeId] || [];
							return !periods.length || periods.some((period) => period.startDate <= value && period.endDate >= value);
						}),
					endDate: Yup.string()
						.required('End date is required')
						.test('end-after-start', 'End date must be on or after the start date', function (value) {
							const { startDate } = this.parent;

							if (!value || !startDate) return true;

							// Both are "YYYY-MM-DD" — lexicographic compare avoids
							// timezone pitfalls of new Date() parsing UTC midnight.
							return value >= startDate;
						})
						.test('within-auth', 'End date must be within the selected authorization period', function (value) {
							if (!value) return true;

							const periods = authBounds[this.parent.sessionTypeId] || [];
							const period = periods.find((item) =>
								item.startDate <= this.parent.startDate && item.endDate >= this.parent.startDate,
							);

							return !period || value <= period.endDate;
						}),
					providerId: Yup.string().required('Please assign a provider'),
					sessionTypeId: Yup.string().required('Please select a session type'),
					timeSlots: Yup.array()
						.of(Yup.object({
							days: Yup.array().of(Yup.number()).min(1, 'Select at least one day'),
							startTime: Yup.string().required('Start time is required'),
							endTime: Yup.string()
								.required('End time is required')
								.test('after-start', 'End time must be after start time', function (value) {
									const { startTime } = this.parent;

									if (!value || !startTime) return true;

									return value > startTime;
								}),
						}))
						.min(1, 'Add at least one time')
						.test('no-overlap', 'Times on the same day cannot overlap', (slots) => !hasOverlappingDailyTimes(slots ?? [])),
				}),
			)
			.min(1, 'Add at least one schedule'),
	});
